import { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../../store/userWishlist/userWishlistSlice';
import { findUserWishlist } from '../../../store/userWishlist/api';
import Loader from '../../../components/Loader';
import Scrollbar from '../../../components/Scrollbar';
import Label from '../../../components/Label';
import { Users } from '../../../store/users/api/interfaces';
import { fDateTime } from '../../../utils/formatTime';

interface IProps {
  user: Users | undefined;
}

const getEntityTypeLabel = (entityType?: string) => {
  if (entityType === 'category_cake') return 'Occasion Cake';
  if (entityType === 'bundle_product') return 'Bundle Product';
  return entityType || '-';
};

const WishlistTab: React.FC<IProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.userWishlist);

  useEffect(() => {
    if (user?.id) {
      dispatch(
        findUserWishlist({
          limit,
          skip,
          userId: user.id,
        })
      );
    }
  }, [dispatch, limit, skip, user]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  return (
    <>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
          <Table size={'medium'}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Type'}</TableCell>
                <TableCell align='center'>{'Product'}</TableCell>
                <TableCell align='center'>{'Category'}</TableCell>
                <TableCell align='center'>{'Price (QAR)'}</TableCell>
                <TableCell align='center'>{'Added on'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((row) => {
                  const item = row.item;
                  const price =
                    item?.discountedPrice && item.discountedPrice > 0
                      ? item.discountedPrice
                      : item?.price;
                  const categoryLabel =
                    item?.category?.labelEn ||
                    item?.subcategory?.labelEn ||
                    '-';

                  return (
                    <TableRow key={row.id}>
                      <TableCell align='center'>
                        {getEntityTypeLabel(row.entityType)}
                      </TableCell>
                      <TableCell align='center'>
                        {item?.labelEn || row.entityId}
                      </TableCell>
                      <TableCell align='center'>{categoryLabel}</TableCell>
                      <TableCell align='center'>
                        {price != null ? parseFloat(String(price)).toFixed(2) : '-'}
                      </TableCell>
                      <TableCell align='center'>
                        {row.createdAt ? fDateTime(row.createdAt) : '-'}
                      </TableCell>
                      <TableCell align='center'>
                        <Label
                          variant='ghost'
                          color={row.available ? 'success' : 'warning'}
                        >
                          {row.available ? 'Available' : 'Unavailable'}
                        </Label>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </>
  );
};

export default WishlistTab;
